import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './BestGraphicsPortfolio.css';

const BestGraphicsPortfolio = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Handle modal functionality
  const openModal = (index) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset'; // Restore scrolling
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % graphics.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + graphics.length) % graphics.length);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen) return;

      switch (e.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        case 'ArrowLeft':
          prevImage();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Graphics data from the public directory
  const graphics = [
    { id: 1, src: '/politicalGraphics/6.jpg', alt: 'Political Graphics P1' },
    { id: 2, src: '/politicalGraphics/7.jpg', alt: 'Political Graphics P2' },
    { id: 3, src: '/politicalGraphics/8.jpg', alt: 'Political Graphics P3' },
    { id: 4, src: '/politicalGraphics/1.jpg', alt: 'Political Graphics 1' },
    { id: 5, src: '/politicalGraphics/2.jpg', alt: 'Political Graphics 2' },
    { id: 6, src: '/politicalGraphics/3.jpg', alt: 'Political Graphics 3' },
    { id: 7, src: '/politicalGraphics/4.jpg', alt: 'Political Graphics 3' },
    { id: 8, src: '/politicalGraphics/5.jpg', alt: 'Political Graphics 3' },

  ];

  // Duplicate graphics array for seamless infinite scroll
  const duplicatedGraphics = [...graphics, ...graphics, ...graphics];

  return (
    <section id="graphics-portfolio" className="py-20 md:py-32 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-2">
        {/* Header */}
        <div className="text-center mb-2">
          <motion.div
            className="inline-flex items-center space-x-3 bg-jetBlue-100 text-jetBlue-800 rounded-full px-6 py-3 mb-8"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2 }}
          >
            <span className="text-xl">🎨</span>
            <span className="font-semibold tracking-wide">CREATIVE EXCELLENCE</span>
          </motion.div>

          <motion.h2
            className="display-text text-gray-900 mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            Best Graphics
            <span className="text-gradient-political block mt-2">Portfolio Showcase</span>
          </motion.h2>

          <motion.p
            className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            Discover our most impactful political graphics that have shaped campaigns
            and influenced public opinion across diverse political landscapes.
          </motion.p>
        </div>


      </div>
      <div class="max-w-8xl mx-auto px-2 sm:px-4 lg:px-2">
         {/* Graphics Portfolio Single Row with Scrolling Animation */}
        <motion.div
          ref={ref}
          className="bg-white rounded-3xl shadow-2xl md:p-2 overflow-hidden"
          initial={{ opacity: 0, y: 8, scale: 0.25 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ duration: 0.2, delay: 0.2 }}
        >
          <div className="relative overflow-hidden h-64 md:h-80">
            {/* Gradient overlays for smooth fade effect */}
            <div className="absolute left-0 top-0 w-6 md:w-20 h-full graphics-fade-left z-10 pointer-events-none"></div>
            <div className="absolute right-0 top-0 w-6 md:w-20 h-full graphics-fade-right z-10 pointer-events-none"></div>

            {/* Single scrolling graphics row */}
            <div className="flex items-center h-full md:h-64 py-2 md:py-4">
              <motion.div
                className="flex items-center space-x-6 md:space-x-8 graphics-scroll graphics-scroll-ltr h-full"
                animate={{
                  x: [0, -((320 + 32) * graphics.length)], // Right to left movement
                }}
                transition={{
                  x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 50, // 50 seconds for complete cycle
                    ease: "linear",
                  },
                }}
                style={{
                  width: `${(320 + 32) * duplicatedGraphics.length}px`, // 320px graphic width + 32px spacing
                  willChange: 'transform'
                }}
              >
                {duplicatedGraphics.map((graphic, index) => {
                  const originalIndex = graphics.findIndex(g => g.id === graphic.id);
                  return (
                    <motion.div
                      key={`${graphic.id}-${index}`}
                      className="flex-shrink-0 graphics-item-responsive h-52 bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 cursor-pointer group graphics-item"
                      whileHover={{
                        scale: 1.05,
                        y: -5,
                        transition: { duration: 0.3 },
                      }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => openModal(originalIndex)}
                    >
                      <div className="w-full h-full">
                        <img
                          src={graphic.src}
                          alt={graphic.alt}
                          className="w-full h-full sm:h-full sm:w-full object-contain overflow-hidden rounded-xl"
                          loading="lazy"
                          onError={(e) => {
                            // Fallback if image fails to load
                            e.target.style.display = 'none';
                            const fallback = e.target.nextElementSibling;
                            if (fallback) {
                              fallback.style.display = 'flex';
                            }
                          }}
                        />
                        {/* Fallback display */}
                        <div className="hidden w-full h-full items-center justify-center text-sm font-bold text-gray-600 bg-gray-100">
                          <span>Graphic {graphic.id}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
          </div>

          {/* Additional info */}
          <div className="text-center mt-2">
            <p className="text-sm text-gray-500">
              Crafted with precision to deliver maximum impact across all political campaigns
            </p>
          </div>
        </motion.div>
      </div>







      {/* Image Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 overflow-x-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <div className="relative max-w-7xl max-h-full p-4 modal-content">
              {/* Close button */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white text-xl font-bold transition-all duration-200"
                aria-label="Close modal"
              >
                ×
              </button>

              {/* Navigation arrows */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white text-2xl font-bold transition-all duration-200"
                aria-label="Previous image"
              >
                ‹
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white text-2xl font-bold transition-all duration-200"
                aria-label="Next image"
              >
                ›
              </button>

              {/* Modal image */}
              <motion.img
                key={currentImageIndex}
                src={graphics[currentImageIndex]?.src}
                alt={graphics[currentImageIndex]?.alt}
                className="max-w-full object-contain rounded-lg modal-image-height"
                style={{
                  height: '80vh',
                  maxHeight: '80vh'
                }}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={(e) => e.stopPropagation()}
              />

              {/* Image counter */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white bg-opacity-20 text-white px-4 py-2 rounded-full text-sm">
                {currentImageIndex + 1} / {graphics.length}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>

  );
};

export default BestGraphicsPortfolio;
